import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CadsHeadingDetailBitComponent } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.component';
import type { CadsHeadingDetailBitConfig } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';

import type { CadsCheckboxGroupConfig, CadsCheckboxGroupOption } from './cads-checkbox-group.config';

/**
 * Reactive-forms checkbox group whose value is a `string[]` of the currently checked option ids (preserved in option
 * order). A single entry in `config.options` is supported for the same UX as a lone consent checkbox (value `[]` or
 * `[id]`). Implements {@link ControlValueAccessor}. Hosts associate it via `formControlName` or `[formControl]` and the
 * component takes ownership of label / helper / error visuals.
 *
 * `host[role]="group"` matches assistive-tech expectations for grouped, independently togglable controls. Option rows
 * may use {@link CadsHeadingDetailBitComponent} via `option.heading` (optional `option.label` when heading supplies copy).
 * Set `config.cardStyle` to **false** for borderless rows inside an outer `cads-card-view` (see styleguide).
 */
@Component({
    selector: 'cads-checkbox-group',
    imports: [CadsHeadingDetailBitComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CadsCheckboxGroupComponent),
            multi: true,
        },
    ],
    templateUrl: './cads-checkbox-group.component.html',
    styleUrl: './cads-checkbox-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-checkbox-group-host',
        role: 'group',
        '[attr.aria-labelledby]': '_groupLabelId()',
        '[attr.aria-describedby]': '_showCaption() ? _captionId() : null',
        '[attr.aria-invalid]': '_hasError() ? "true" : null',
        '[attr.aria-required]': '_ariaRequired()',
    }
})
export class CadsCheckboxGroupComponent implements ControlValueAccessor {
  readonly config = input.required<CadsCheckboxGroupConfig>();

  private readonly _fallbackGroupId = nextCadsFormKitId('cads-checkbox-group');

  protected readonly _selected = signal<readonly string[]>([]);
  protected readonly _controlDisabled = signal(false);

  protected readonly _groupId = computed(() => {
    const fromConfig = this.config().groupId?.trim();
    return fromConfig || this._fallbackGroupId;
  });

  protected readonly _groupLabelId = computed(() => (this.config().label ? `${this._groupId()}-label` : null));

  protected readonly _captionId = computed(() => `${this._groupId()}-caption`);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  protected readonly _ariaRequired = computed(() => (this.config().required === true ? 'true' : null));

  protected readonly _showHelper = computed(() => {
    if (this._hasError()) {
      return false;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _showCaption = computed(() => this._hasError() || this._showHelper());

  protected readonly _captionText = computed(() => {
    if (this._hasError()) {
      return (this.config().errorMessage ?? '').trim();
    }
    return this.config().helperText?.trim() ?? '';
  });

  protected readonly _groupDisabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  private _onChange: (value: string[]) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: readonly string[] | null | undefined): void {
    if (value === null || value === undefined) {
      this._selected.set([]);
      return;
    }
    this._selected.set([...value]);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
  }

  protected _isChecked(option: CadsCheckboxGroupOption): boolean {
    return this._selected().includes(option.id);
  }

  protected _isDisabled(option: CadsCheckboxGroupOption): boolean {
    return this._groupDisabled() || option.disabled === true;
  }

  protected _rowInputId(option: CadsCheckboxGroupOption): string {
    return `${this._groupId()}-${option.id}`;
  }

  protected _onRowChange(option: CadsCheckboxGroupOption, event: Event): void {
    if (this._isDisabled(option)) {
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    const order = this.config().options.map((o) => o.id);
    const current = new Set(this._selected());
    if (checked) {
      current.add(option.id);
    } else {
      current.delete(option.id);
    }
    const next = order.filter((id) => current.has(id));
    this._selected.set(next);
    this._onChange(next);
    this._onTouched();
  }

  protected _heading(option: CadsCheckboxGroupOption): CadsHeadingDetailBitConfig | null {
    return option.heading ?? null;
  }

  protected _subtitleOnly(option: CadsCheckboxGroupOption): string | null {
    if (option.heading) {
      return null;
    }
    const t = option.subtitleOnly?.trim();
    return t ? t : null;
  }

  protected _cardStyle(option: CadsCheckboxGroupOption): boolean {
    if (this.config().cardStyle === false) {
      return false;
    }
    return option.cardStyle !== false;
  }

  /** Accessible name for a row input: heading title, then subtitle-only, then plain label. */
  protected _rowAriaLabel(option: CadsCheckboxGroupOption): string {
    const fromHeading = option.heading?.title?.trim();
    if (fromHeading) {
      return fromHeading;
    }
    const sub = option.subtitleOnly?.trim();
    if (sub) {
      return sub;
    }
    return option.label?.trim() ?? '';
  }
}
